/**
 * Announcement config — the extensible, per-UI-component payload attached to a
 * feature-flag announcement.
 *
 * This is the single source of truth shared by:
 *   - the API (superadmin route body validation + the viewer read endpoint)
 *   - the web SPA (the component registry that renders each announcement)
 *
 * Extensibility: `uiComponent` is a discriminated union. Adding a future
 * surface (e.g. a `custom_view`) is one new `z.object({ uiComponent:
 * z.literal('…'), … })` member here plus one component in the web registry —
 * nothing else changes. The default (and only initial) surface is the
 * interstitial dialog.
 */
import { z } from 'zod';

/** How a viewer's "seen" state is de-duplicated for an announcement. */
export const ANNOUNCEMENT_SEEN_SCOPES = ['user', 'org'] as const;
export type AnnouncementSeenScope = (typeof ANNOUNCEMENT_SEEN_SCOPES)[number];

/** Known UI component kinds. Extend this union to add a new surface. */
export const ANNOUNCEMENT_UI_COMPONENTS = ['interstitial_dialog'] as const;
export type AnnouncementUiComponent = (typeof ANNOUNCEMENT_UI_COMPONENTS)[number];

/**
 * Config for the default interstitial-dialog surface. `body` is rendered as
 * plain text for now (mirrors the Hints content surface); markdown is a
 * forward-compatible enhancement.
 */
export const interstitialDialogConfigSchema = z.object({
  uiComponent: z.literal('interstitial_dialog'),
  title: z.string().min(1).max(200),
  body: z.string().min(1).max(10_000),
  /** Optional call-to-action button. Both label and href required together. */
  ctaLabel: z.string().min(1).max(80).optional(),
  ctaHref: z.string().url().optional(),
  /** Overrides the default "Got it" dismiss button label. */
  dismissLabel: z.string().min(1).max(80).optional(),
});

/**
 * The discriminated union of every announcement UI-component config. Add future
 * variants as additional members keyed by a new `uiComponent` literal.
 */
export const announcementConfigSchema = z.discriminatedUnion('uiComponent', [
  interstitialDialogConfigSchema,
]);

export type AnnouncementConfig = z.infer<typeof announcementConfigSchema>;
export type InterstitialDialogConfig = z.infer<typeof interstitialDialogConfigSchema>;

/**
 * One announcement as delivered to a viewer by `GET /announcements/active`.
 * Deliberately slim — only what the renderer needs. The `config` discriminates
 * on `uiComponent`, so the frontend can pick a component without extra fields.
 */
export interface ActiveAnnouncement {
  id: string;
  uiComponent: AnnouncementUiComponent;
  config: AnnouncementConfig;
}
